
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useApolloClient } from "@apollo/client";
import { REGISTER_MUTATION, COUNTRY_CODE_LIST, GET_COUNTRY_LIST, GET_STATE_LIST } from "@/Graphql/Queries";
import { CHECK_REGISTRATION } from "@/Graphql/AuthQueries";
import { GENERATE_OTP } from "@/Graphql/otpQueries";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
// import { toast } from "react-toastify";
import { dynamicMediaUrl } from "@/Graphql/apolloClient";
import { isValidEmail, isValidPhoneNumber, isValidUsername } from "@/utils/validationUtils";
import { useToast } from '@/hooks/use-toast';

// Password validation function
const isValidPassword = (password: string) => {
  const minLength = 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return password.length >= minLength && hasUppercase && hasLowercase && hasNumbers && hasSpecialChar;
};

const getPasswordValidationMessage = (password: string) => {
  if (!password) return "Password is required";

  const requirements = [];
  if (password.length < 8) requirements.push("at least 8 characters");
  if (!/[A-Z]/.test(password)) requirements.push("one uppercase letter");
  if (!/[a-z]/.test(password)) requirements.push("one lowercase letter");
  if (!/\d/.test(password)) requirements.push("one number");
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) requirements.push("one special character");

  if (requirements.length > 0) {
    return `Password must contain ${requirements.join(", ")}`;
  }

  return "";
};


interface SignUpProps {
  registrationFields: { key: string; default: boolean }[];
  loading: boolean;
  yourLogo?: string;
}


// Helper to check if a field is enabled based on registrationFields config
const isFieldEnabled = (registrationFields: { key: string; default: boolean }[], key: string) => {
  const field = registrationFields.find((f) => f.key.toLowerCase() === key.toLowerCase());
  return field ? field.default : true;
};


// Helper to check if login is enabled in Landing Page config (with configLoaded)
const useLandingLoginConfig = () => {
  const [configData, setConfigData] = React.useState<any[]>([]);
  const [configLoaded, setConfigLoaded] = React.useState(false);

  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`);
        if (!response.ok) throw new Error("Failed to fetch configJson");
        const data = await response.json();
        setConfigData(data);
        setConfigLoaded(true);
      } catch (e) {
        setConfigLoaded(true); // Still set loaded to avoid infinite loading
      }
    };
    fetchConfig();
  }, []);

  const landingPage = configData?.find((page: any) => page.name === "Landing Page");
  const landingConfigs = landingPage && Array.isArray(landingPage.configurations) ? landingPage.configurations : [];
  const loginConfig = landingConfigs.find((cfg: any) => cfg.key === "login");
  const isLoginEnabled = loginConfig ? loginConfig.default !== false : true;
  return { configLoaded, isLoginEnabled };
};

// Custom Country Dropdown Component
interface CountryDropdownProps {
  countryCodeOptions: any[];
  selectedCountryCode: string;
  selectedCountryName: string;
  setSelectedCountryCode: (code: string) => void;
  setSelectedCountryName: (name: string) => void;
  setSelectedCountryId: (id: string) => void;
  disabled: boolean;
  isCompanyPhone?: boolean;
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  countryCodeOptions,
  selectedCountryCode,
  selectedCountryName,
  setSelectedCountryCode,
  setSelectedCountryName,
  setSelectedCountryId,
  disabled,
  isCompanyPhone = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredCountries = countryCodeOptions.filter((item: any) => {
    // item.country.toLowerCase().startsWith(searchQuery.toLowerCase())
    const q = searchQuery.toLowerCase();

    return (
      item.country?.toLowerCase().includes(q) ||   // search by country name
      item.code?.toLowerCase().includes(q) ||      // search by +63
      item.code?.replace("+", "").includes(q)      // search by 63 only
    );
  });

  const handleSelect = (item: any) => {
    setSelectedCountryCode(item.code);
    setSelectedCountryName(item.country);
    setSelectedCountryId(item.id || "");
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div className={`relative ${isCompanyPhone ? "md:w-32 w-full" : "w-32"}`} ref={dropdownRef}>
      <div
        className={`flex items-center justify-between w-full h-12 px-2 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm cursor-pointer ${disabled ? "opacity-50" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="flex-1 text-center">
          {selectedCountryCode}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </div>
      {isOpen && (
        <div className="absolute z-10 w-80 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto overflow-x-hidden">
          <div className="p-2">
            <Input
              type="text"
              placeholder="Search country"
              value={searchQuery}
              onChange={(e) => {
                const q = e.target.value.toLowerCase();
                setSearchQuery(e.target.value);
                const firstMatch = countryCodeOptions.find((item: any) =>
                  // item.country.toLowerCase().startsWith(e.target.value.toLowerCase())

                  item.country?.toLowerCase().includes(q) ||
                  item.code?.toLowerCase().includes(q) ||
                  item.code?.replace("+", "").includes(q)
                );
                if (firstMatch && e.target.value) {
                  setSelectedCountryCode(firstMatch.code);
                  setSelectedCountryName(firstMatch.country);
                  setSelectedCountryId(firstMatch.id || "");
                }
              }}
              className="w-full h-10 px-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-emerald-500 focus:ring-0"
              autoFocus
            />
          </div>
          <ul className="divide-y divide-gray-100">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((item: any, idx: number) => (
                <li
                  key={item.code + item.country + idx}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 white-space-nowrap ${
                    item.country === selectedCountryName
                      ? "text-blue-600 font-semibold"
                      : ""
                    }`}
                  onClick={() => handleSelect(item)}
                >
                  {item.code} - {item.country}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-sm text-gray-500 white-space-nowrap">
                No matching countries
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// Custom Country Dropdown Component (for company details)
interface CustomCountryDropdownProps {
  countryOptions: any[];
  selectedCountry: string;
  setSelectedCountry: (name: string) => void;
  setSelectedCountryId: (id: string) => void;
  disabled: boolean;
}

const CustomCountryDropdown: React.FC<CustomCountryDropdownProps> = ({
  countryOptions,
  selectedCountry,
  setSelectedCountry,
  setSelectedCountryId,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // const filteredCountries = countryOptions.filter((item: any) =>
    // item.name.toLowerCase().startsWith(searchQuery.toLowerCase())
  
  // );

  const filteredCountries = countryOptions.filter((item: any) => {
    const q = searchQuery.toLowerCase().replace("+", "");

    const nameMatch = item.name?.toLowerCase().includes(q);
    const countryMatch = item.country?.toLowerCase().includes(q);
    const dialingCodeMatch = item.dialingCode?.replace("+", "").includes(q);
    const codeMatch = item.code?.replace("+", "").includes(q);

    return nameMatch || countryMatch || dialingCodeMatch || codeMatch;
  });

  const handleSelect = (item: any) => {
    setSelectedCountry(item.name);
    setSelectedCountryId(item.id || "");
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`flex items-center justify-between w-full h-12 px-2 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm cursor-pointer ${disabled ? "opacity-50" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="flex-1 truncate">
          { selectedCountry || "Country" }
        </span>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto overflow-x-hidden">
          <div className="p-2">
            <Input
              type="text"
              placeholder="Search country"
              value={searchQuery}
              onChange={(e) => {
                const q = e.target.value.toLowerCase();
                setSearchQuery(e.target.value);
                /*const firstMatch = countryOptions.find((item: any) =>
                  item.name.toLowerCase().startsWith(e.target.value.toLowerCase())  
                );*/

                const normalized = q.replace("+", "");

                const firstMatch = countryOptions.find((item: any) =>
                  item.name?.toLowerCase().includes(q) ||
                  item.country?.toLowerCase().includes(q) ||
                  item.dialingCode?.replace("+", "").includes(normalized)
                );
                if (firstMatch && e.target.value) {
                  setSelectedCountry(firstMatch.name);
                  setSelectedCountryId(firstMatch.id || "");
                }

                
              }}
              className="w-full h-10 px-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-emerald-500 focus:ring-0"
              autoFocus
            />
          </div>
          <ul className="divide-y divide-gray-100">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((item: any, idx: number) => (
                <li
                  key={item.id + item.name + idx}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 white-space-nowrap ${
                    item.name === selectedCountry
                      ? "text-blue-600 font-semibold"
                      : ""
                  }`}
                  onClick={() => handleSelect(item)}
                >
                  {item.dialingCode} - {item.name}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-sm text-gray-500 white-space-nowrap">
                No matching countries
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// Custom State Dropdown Component (for company details)
interface CustomStateDropdownProps {
  stateOptions: any[];
  selectedState: string;
  setSelectedState: (name: string) => void;
  disabled: boolean;
}

const CustomStateDropdown: React.FC<CustomStateDropdownProps> = ({
  stateOptions,
  selectedState,
  setSelectedState,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredStates = stateOptions.filter((item: any) =>
    item.name.toLowerCase().startsWith(searchQuery.toLowerCase())
  );

  const handleSelect = (item: any) => {
    setSelectedState(item.name);
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`flex items-center justify-between w-full h-12 px-2 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm cursor-pointer ${disabled ? "opacity-50" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="flex-1 truncate">
          {selectedState || "State"}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto overflow-x-hidden">
          <div className="p-2">
            <Input
              type="text"
              placeholder="Search state"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                const firstMatch = stateOptions.find((item: any) =>
                  item.name.toLowerCase().startsWith(e.target.value.toLowerCase())
                );
                if (firstMatch && e.target.value) {
                  setSelectedState(firstMatch.name);
                }
              }}
              className="w-full h-10 px-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-emerald-500 focus:ring-0"
              autoFocus
            />
          </div>
          <ul className="divide-y divide-gray-100">
            {filteredStates.length > 0 ? (
              filteredStates.map((item: any, idx: number) => (
                <li
                  key={item.stateId + item.name + idx}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 white-space-nowrap ${
                    item.name === selectedState
                      ? "text-blue-600 font-semibold"
                      : ""
                  }`}
                  onClick={() => handleSelect(item)}
                >
                  {item.name}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-sm text-gray-500 white-space-nowrap">
                No matching states
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const Register: React.FC<SignUpProps> = ({ registrationFields, loading, yourLogo }) => {
  // Use landing config for login visibility (like cart page)
  const { configLoaded, isLoginEnabled } = useLandingLoginConfig();
  const { toast } = useToast();
  // Fetch country codes from API
  const { data: countryCodeData, loading: countryCodeLoading } = useQuery(COUNTRY_CODE_LIST);
  const countryCodeOptions = countryCodeData?.countryCodeList || [];

  // Fetch country list for company details
  const { data: countryListData, loading: countryListLoading } = useQuery(GET_COUNTRY_LIST);
  const countryOptions = countryListData?.countryList || [];

  // State list is dependent on selected country
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const { data: stateListData, loading: stateListLoading } = useQuery(GET_STATE_LIST, {
    variables: { countryId: selectedCountryId },
    skip: !selectedCountryId,
  });
  const stateOptions = stateListData?.stateList || [];

  // State for mobile number country code selection
  const [mobileCountryCode, setMobileCountryCode] = useState("+65");
  const [mobileCountryName, setMobileCountryName] = useState("Singapore");

  // State for company phone country code selection
  const [companyPhoneCountryCode, setCompanyPhoneCountryCode] = useState("+65");
  const [companyPhoneCountryName, setCompanyPhoneCountryName] = useState("Singapore");

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companyName: "",
    companyId: "",
    companyPhone: "",
    designation: "",
    industry: "",
    country: "",
    state: "",
    address: "",
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerUser, { loading: isRegisterPending }] = useMutation(REGISTER_MUTATION);
  const [generateOTP, { loading: isGeneratingOTP }] = useMutation(GENERATE_OTP);
  const client = useApolloClient();
  const companyId = import.meta.env.VITE_APP_COMPANY_ID;

  // Step 1 required fields
  const requiredStep1Fields: (keyof typeof formData)[] = [
    "firstName",
    "lastName",
    "email",
    "username"
  ];

  // Check if all required fields are filled and valid (no errors)
  const isStep1Valid = requiredStep1Fields.every(
    (field) => formData[field].trim() && !errors[field]
  );

  // Step 2 required fields
  const requiredStep2Fields: (keyof typeof formData)[] = [
    "companyName",
    "country",
    "state",
  ];

  const isStep2Valid = requiredStep2Fields.every(
    (field) => formData[field].trim() && !errors[field]
  );

  // Step 3 required fields
  const requiredStep3Fields: (keyof typeof formData)[] = [
    "password",
    "confirmPassword"
  ];

  const isStep3Valid = requiredStep3Fields.every(
    (field) => formData[field].trim() && !errors[field]
  );

  // Handle country code selection
  /*const handleCountryCodeChange = (newCountryCode: string) => {
    const selectedCountry = countryCodeOptions.find((item: any) => item.code === newCountryCode);
    if (selectedCountry) {
      setMobileCountryCode(newCountryCode);
      setMobileCountryName(selectedCountry.country);
      setSelectedCountryId(selectedCountry.id || "");
    }

    // Re-validate mobile number with new country code if mobile exists
    if (formData.mobile) {
      validateField("mobile", formData.mobile).then(mobileErrors => {
        setErrors(prev => ({ ...prev, ...mobileErrors }));
      });
    }

    // Re-validate company phone with new country code if companyPhone exists
    if (formData.companyPhone) {
      validateField("companyPhone", formData.companyPhone).then(companyPhoneErrors => {
        setErrors(prev => ({ ...prev, ...companyPhoneErrors }));
      });
    }
  };*/

  // Handle country code selection
const handleCountryCodeChange = (newCountryCode: string) => {
  const selectedCountry = countryCodeOptions.find(
    (item: any) => item.code === newCountryCode
  );

  if (selectedCountry) {
    setMobileCountryCode(newCountryCode);
    setMobileCountryName(selectedCountry.country);
    setSelectedCountryId(selectedCountry.id || "");
  }

  // ⬇️ CLEAR mobile & companyPhone errors FIRST
  setErrors(prev => ({
    ...prev,
    mobile: "",
    companyPhone: ""
  }));

  // ⬇️ Re-validate ONLY if user already typed something (optional)
  // Add a small delay to allow state update to clear the error first
  setTimeout(() => {
    if (formData.mobile) {
      validateField("mobile", formData.mobile).then(mobileErrors => {
        setErrors(prev => ({ ...prev, ...mobileErrors }));
      });
    }

    if (formData.companyPhone) {
      validateField("companyPhone", formData.companyPhone).then(companyPhoneErrors => {
        setErrors(prev => ({ ...prev, ...companyPhoneErrors }));
      });
    }
  }, 50);
};

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Please enter your valid first name";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Please enter your valid last name";
    }
    // Mobile is optional - only validate if provided
    if (formData.mobile.trim() && !isValidPhoneNumber(mobileCountryCode + formData.mobile)) {
      newErrors.mobile = "Invalid phone number format";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your valid email ID";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.username.trim()) {
      newErrors.username = "Please enter a valid profile name";
    } else if (!isValidUsername(formData.username)) {
      newErrors.username = "Username must be at least 4 characters and contain only letters, numbers, or underscores";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }
    // if (!formData.companyId.trim()) {
    //   newErrors.companyId = "Company ID is required";
    // }
    if (formData.companyPhone.trim() && !isValidPhoneNumber(companyPhoneCountryCode + formData.companyPhone)) {
      newErrors.companyPhone = "Invalid company phone number format";
    }
    // if (!formData.industry.trim()) {
    //   newErrors.industry = "Industry is required";
    // }
    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }
    // if (!formData.address.trim()) {
    //   newErrors.address = "Company address is required";
    // }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateField = async (field: string, value: string) => {
    const newErrors: Record<string, string> = {};

    switch (field) {
      case "firstName":
        if (!value.trim()) {
          newErrors.firstName = "Please enter your valid first name";
        }
        break;
      case "lastName":
        if (!value.trim()) {
          newErrors.lastName = "Please enter your valid last name";
        }
        break;
      case "mobile":
        // Mobile is optional - only validate if provided
        if (value.trim() && !isValidPhoneNumber(mobileCountryCode + value)) {
          newErrors.mobile = "Invalid phone number format";
        }
        break;
      case "email":
        if (!value.trim()) {
          newErrors.email = "Please enter your valid email ID";
        } else if (!isValidEmail(value)) {
          newErrors.email = "Invalid email format";
        }
        break;
      case "username":
        if (!value.trim()) {
          newErrors.username = "Please enter a valid profile name";
        } else if (!isValidUsername(value)) {
          newErrors.username = "Username must be at least 4 characters and contain only letters, numbers, or underscores";
        }
        break;
      case "companyName":
        if (!value.trim()) {
          newErrors.companyName = "Company name is required";
        }
        break;
      // case "companyId":
      //   if (!value.trim()) {
      //     newErrors.companyId = "Company ID is required";
      //   }
      //   break;
      case "companyPhone":
        if (value.trim() && !isValidPhoneNumber(companyPhoneCountryCode + value)) {
          newErrors.companyPhone = "Invalid company phone number format";
        }
        break;
      // case "industry":
      //   if (!value.trim()) {
      //     newErrors.industry = "Industry is required";
      //   }
      //   break;
      case "country":
        if (!value.trim()) {
          newErrors.country = "Country is required";
        }
        break;
      case "state":
        if (!value.trim()) {
          newErrors.state = "State is required";
        }
        break;
      // case "address":
      //   if (!value.trim()) {
      //     newErrors.address = "Company address is required";
      //   }
      //   break;
      case "password":
        const passwordError = getPasswordValidationMessage(value);
        if (passwordError) {
          newErrors.password = passwordError;
        }
        break;
      case "confirmPassword":
        if (value !== formData.password) {
          newErrors.confirmPassword = "Passwords do not match";
        }
        break;
    }

    return newErrors;
  };

  const validateFieldWithDatabase = async (field: string, value: string) => {
    const newErrors: Record<string, string> = {};
    const companyId = import.meta.env.VITE_APP_COMPANY_ID || "5819b2e13ce70e0e2a9c318e"; // Ensure companyId is defined

    switch (field) {
      case "email":
        if (!value.trim()) {
          newErrors.email = "Please enter your valid email ID";
        } else if (!isValidEmail(value)) {
          newErrors.email = "Invalid email format";
        } else {
          try {
            const { data } = await client.mutate({ // Changed from client.query to client.mutate since it's a mutation
              mutation: CHECK_REGISTRATION, // Specify as mutation
              variables: {
                companyId,
                field: "email",
                value,
              },
            });
            const result = data?.checkRegistration?.results?.[0];
            if (result && !result.valid && result.errors && result.errors.length > 0) {
              newErrors.email = result.errors[0]; // Extract the specific error message from API
              console.warn("Validation errors found:", result.errors);
            } else {
              newErrors.email = ""; // Clear error if valid
            }
          } catch (err) {
            console.error("Validation error for email:", err); // Add logging for debugging
            newErrors.email = "Unable to validate email";
          }
        }
        break;
      case "username":
        if (!value.trim()) {
          newErrors.username = "Please enter a valid profile name";
        } else if (!isValidUsername(value)) {
          newErrors.username = "Username must be at least 4 characters and contain only letters, numbers, or underscores";
        } else {
          try {
            const { data } = await client.mutate({ // Changed from client.query to client.mutate since it's a mutation
              mutation: CHECK_REGISTRATION, // Specify as mutation
              variables: {
                companyId,
                field: "username",
                value,
              },
            });
            const result = data?.checkRegistration?.results?.[0];
            console.log("result:", result);

            if (result && !result.valid && result.errors) {
              console.log("Validation errors found:", result.errors);
              newErrors.username = result.errors[0]; // Extract the specific error message from API
            } else {
              newErrors.username = ""; // Clear error if valid
            }
          } catch (err) {
            console.error("Validation error for username:", err); // Add logging for debugging
            newErrors.username = "Unable to validate username";
          }
        }
        break;
    }
    return newErrors;
  };


  const handleInputChange = async (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validate the field immediately
    let fieldErrors = await validateField(field, value);

    // For email and username, validate with database
    if (field === "email" || field === "username") {
      const dbErrors = await validateFieldWithDatabase(field, value);
      fieldErrors = { ...fieldErrors, ...dbErrors };
    }

    // For country, update countryId and country code, and validate country
    if (field === "country") {
      const selectedCountry = countryOptions.find((c: any) => c.name === value);
      if (selectedCountry) {
        setSelectedCountryId(selectedCountry.id);
        const codeObj = countryCodeOptions.find((item: any) => item.country === selectedCountry.name);
        if (codeObj) {
          setCompanyPhoneCountryCode(codeObj.code);
          setCompanyPhoneCountryName(codeObj.country);
          if (formData.companyPhone) {
            const updatedCompanyPhoneErrors = await validateField("companyPhone", formData.companyPhone);
            fieldErrors = { ...fieldErrors, ...updatedCompanyPhoneErrors };
          }
        }
      } else {
        setSelectedCountryId("");
      }
      setFormData(prev => ({ ...prev, state: "" }));
      // Validate country field
      const countryErrors = await validateField("country", value);
      fieldErrors = { ...fieldErrors, ...countryErrors };
    }

    // For mobile, validate with selected country code
    if (field === "mobile") {
      const mobileErrors = await validateField("mobile", value);
      fieldErrors = { ...fieldErrors, ...mobileErrors };
    }

    // For companyPhone, validate with selected country code
    if (field === "companyPhone") {
      const companyPhoneErrors = await validateField("companyPhone", value);
      fieldErrors = { ...fieldErrors, ...companyPhoneErrors };
    }

    // For password confirmation
    if (field === "password" && formData.confirmPassword) {
      const confirmErrors = await validateField("confirmPassword", formData.confirmPassword);
      fieldErrors = { ...fieldErrors, ...confirmErrors };
    }

    setErrors(prev => ({ ...prev, ...fieldErrors, [field]: fieldErrors[field] || "" }));
  };

  const validateStep3 = () => {
    const newErrors: Record<string, string> = {};

    const passwordError = getPasswordValidationMessage(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      // Clear password fields when moving to step 3
      setFormData(prev => ({
        ...prev,
        password: "",
        confirmPassword: ""
      }));
      // Clear any existing password errors
      setErrors(prev => ({
        ...prev,
        password: "",
        confirmPassword: ""
      }));
      setCurrentStep(3);
    } else if (currentStep === 3 && validateStep3()) {
      // Submit registration with all fields
      try {
        const response = await registerUser({ 
          variables: {
            input: {
        login: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        activated: false,
        langKey: "en",
        password: formData.password,
        companyType: "MEDIA_BUYERS",
        platform: "ECOMMERCE",
        userCompanyAddress: formData.address,
        phone: mobileCountryCode + formData.mobile,
        jobTitle: formData.designation,
        userCountry: formData.country,
        userCity: formData.state,
        industry: formData.industry,
        rejectReason: "",
              ecommerceDomainName: dynamicMediaUrl,
        userCompanyName: formData.companyName,
        userCompanyId: formData.companyId,
        userCompanyPhoneNumber: companyPhoneCountryCode + formData.companyPhone,
        admin: false,
              companyId: companyId,
            }
          }
        });
        console.log("Registration success:", response);
        
        const registrationData = response.data?.createUserOtp;
        
        if (registrationData?.success && registrationData?.id) {
          // Generate OTP after successful registration
      try {
            const otpResponse = await generateOTP({
              variables: {
                userId: registrationData.id,
                event: "REGISTRATION",
                channel: "EMAIL"
              }
            });
            
            if (otpResponse.data?.generateOtp?.success) {
        toast({
          title: "Success",
                description: "Registration successful! OTP sent to your email.",
                variant: "default",
              });
              
              // Navigate to OTP verification page with user data
              navigate("/verify-email", {
                state: {
                  userId: registrationData.id,
                  email: registrationData.email,
                  companyId: registrationData.companyId,
                  expirySecond: otpResponse.data.generateOtp.expirySecond
                }
              });
            } else {
              toast({
                title: "Registration Successful",
                description: "Registration completed, but failed to send OTP. Please contact support.",
          variant: "default",
        });
        navigate("/login");
            }
          } catch (otpError) {
            console.error("OTP generation error:", otpError);
            toast({
              title: "Registration Successful",
              description: "Registration completed, but failed to send OTP. Please contact support.",
              variant: "default",
            });
            navigate("/login");
          }
        } else {
          toast({
            title: "Error", 
            description: registrationData?.message || "Registration failed. Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Registration error:", error);
        toast({
          title: "Error",
          description: "Registration failed. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Left Side - Branding */}
          <div className="lg:w-1/2 bg-white p-8 lg:p-12 flex flex-col justify-center">
            <div className="mb-8 flex items-center">
              <Link to="/" className="flex items-center">
                {yourLogo ? (
                  <img
                    src={yourLogo}
                    alt="Logo"
                    className="h-10 w-auto"
                    style={{ maxWidth: 160 }}
                  />
                ) : (
                  <>
                    <div className="w-10 h-10 bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7v10c0 5.55 3.84 10 9 11 5.16-1 9-5.45 9-11V7l-10-5zM12 9c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z" />
                      </svg>
                    </div>
                    <span className="ml-3 text-xl font-semibold text-gray-900">BillboardHub</span>
                  </>
                )}
              </Link>
            </div>
            <div className="space-y-6">
              <h1 className="text-3xl lg:text-4xl font-bold text-emerald-600 leading-tight">
                Welcome to OOH<br />
                Media Marketplace
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Run your next outdoor<br />
                Advertising campaign with just<br />
                a few clicks
              </p>
            </div>
          </div>

          {/* Right Side - Registration Form */}
          <div className="lg:w-1/2 bg-gray-50 p-8 lg:p-12 flex flex-col justify-center">
            <div className="max-w-md mx-auto w-full">
              <form onSubmit={e => { e.preventDefault(); handleNext(); }} autoComplete="off">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-emerald-600 mb-2">
                    {currentStep === 1
                      ? "Basic details"
                      : currentStep === 2
                        ? "Company details"
                        : "Set your password"}
                  </h2>
                </div>

                {currentStep === 1 ? (
                  <div className="space-y-6">
                    {/* First Name */}
                    {isFieldEnabled(registrationFields, "first name") && (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder="First Name"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange("firstName", e.target.value)}
                          className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        {errors.firstName && (
                          <p className="text-sm text-red-500">{errors.firstName}</p>
                        )}
                      </div>
                    )}
                    {/* Last Name */}
                    {isFieldEnabled(registrationFields, "last name") && (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder="Last Name"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                          className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        {errors.lastName && (
                          <p className="text-sm text-red-500">{errors.lastName}</p>
                        )}
                      </div>
                    )}
                    {/* Mobile Number with Country Code Selector (from API) */}
                    {isFieldEnabled(registrationFields, "phone number with Country Code") && (
                      <div className="space-y-2">
                        <div className="flex w-full gap-2">
                          {/* Country Code Dropdown */}
                          <CountryDropdown
                            countryCodeOptions={countryCodeOptions}
                            selectedCountryCode={mobileCountryCode}
                            selectedCountryName={mobileCountryName}
                            // setSelectedCountryCode={setMobileCountryCode}
                            setSelectedCountryCode={(code: string) => handleCountryCodeChange(code)}
                            setSelectedCountryName={setMobileCountryName}
                            setSelectedCountryId={setSelectedCountryId}
                            disabled={countryCodeLoading}
                          />
                          {/* Phone Number Input */}
                          <div className="flex-1">
                            <Input
                              type="text"
                              placeholder="Mobile Number"
                              value={formData.mobile}
                              onChange={(e) => handleInputChange("mobile", e.target.value)}
                              className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                            />
                          </div>
                        </div>
                        {errors.mobile && (
                          <p className="text-sm text-red-500">{errors.mobile}</p>
                        )}
                      </div>
                    )}
                    {/* Email ID */}
                    {isFieldEnabled(registrationFields, "email id") && (
                      <div className="space-y-2">
                        <Input
                          type="email"
                          placeholder="Email ID"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        {errors.email && (
                          <p className="text-sm text-red-500">{errors.email}</p>
                        )}
                      </div>
                    )}
                    {/* User name */}
                    <div className="space-y-2">
                      <Input
                        type="text"
                        placeholder="User Name"
                        value={formData.username}
                        onChange={(e) => handleInputChange("username", e.target.value)}
                        className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                      />
                      {errors.username && (
                        <p className="text-sm text-red-500">{errors.username}</p>
                      )}
                    </div>
                  </div>
                ) : currentStep === 2 ? (
                  <div className="space-y-6">
                    {/* Company Name */}
                    {isFieldEnabled(registrationFields, "business name") && (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder="Company Name"
                          value={formData.companyName}
                          onChange={(e) => handleInputChange("companyName", e.target.value)}
                          className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        {errors.companyName && (
                          <p className="text-sm text-red-500">{errors.companyName}</p>
                        )}
                      </div>
                    )}
                    {/* Designation and Industry responsive logic */}
                    {isFieldEnabled(registrationFields, "designation") ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Input
                            type="text"
                            placeholder="Designation"
                            value={formData.designation}
                            onChange={(e) => handleInputChange("designation", e.target.value)}
                            className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                          />
                          {errors.designation && (
                            <p className="text-sm text-red-500">{errors.designation}</p>
                          )}
                        </div>
                        {isFieldEnabled(registrationFields, "industry") && (
                        <div className="space-y-2">
                          <Input
                            type="text"
                            placeholder="Industry"
                            value={formData.industry}
                            onChange={(e) => handleInputChange("industry", e.target.value)}
                            className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                          />
                          {errors.industry && (
                            <p className="text-sm text-red-500">{errors.industry}</p>
                          )}
                        </div>
                        )}
                      </div>
                    ) : (
                    isFieldEnabled(registrationFields, "industry") && (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder="Industry"
                          value={formData.industry}
                          onChange={(e) => handleInputChange("industry", e.target.value)}
                          className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        {errors.industry && (
                          <p className="text-sm text-red-500">{errors.industry}</p>
                        )}
                      </div>
                    )
                    )}
                    {/* Country and State Dropdowns (Dynamic) in a responsive grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <CustomCountryDropdown
                          countryOptions={countryOptions}
                          selectedCountry={formData.country}
                          setSelectedCountry={(value) => handleInputChange("country", value)}
                          setSelectedCountryId={setSelectedCountryId}
                          disabled={countryListLoading}
                        />
                        {errors.country && (
                          <p className="text-sm text-red-500">{errors.country}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <CustomStateDropdown
                          stateOptions={stateOptions}
                          selectedState={formData.state}
                          setSelectedState={(value) => handleInputChange("state", value)}
                          disabled={stateListLoading || !selectedCountryId}
                        />
                        {errors.state && (
                          <p className="text-sm text-red-500">{errors.state}</p>
                        )}
                      </div>
                    </div>
                    {/* Company Address and Company ID side by side in a responsive grid */}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {isFieldEnabled(registrationFields, "companyAddress") && (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder="Company Address"
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        {errors.address && (
                          <p className="text-sm text-red-500">{errors.address}</p>
                        )}
                      </div>
                      )}

                      {isFieldEnabled(registrationFields, "companyId") && (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder="Company ID"
                          value={formData.companyId}
                          onChange={(e) => handleInputChange("companyId", e.target.value)}
                          className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        {errors.address && (
                          <p className="text-sm text-red-500">{errors.address}</p>
                        )}
                      </div>
                      )}
                    </div>
                    {/* Company Phone Number */}
                    {isFieldEnabled(registrationFields, "business phone number") && (
                      <div className="space-y-2">
                        <div className="flex flex-col md:flex-row gap-2">
                          {/* Country Code Dropdown for Company Phone */}
                          <CountryDropdown
                            countryCodeOptions={countryCodeOptions}
                            selectedCountryCode={companyPhoneCountryCode}
                            selectedCountryName={companyPhoneCountryName}
                            // setSelectedCountryCode={setCompanyPhoneCountryCode}
                            setSelectedCountryCode={(code: string) => handleCountryCodeChange(code)}
                            setSelectedCountryName={setCompanyPhoneCountryName}
                            setSelectedCountryId={setSelectedCountryId}
                            disabled={countryCodeLoading}
                            isCompanyPhone
                          />
                          {/* Company Phone Input */}
                          <div className="flex-1">
                            <Input
                              type="text"
                              placeholder="Company's phone number"
                              value={formData.companyPhone}
                              onChange={(e) => handleInputChange("companyPhone", e.target.value)}
                              className="w-full h-12 px-4 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                            />
                          </div>
                        </div>
                        {errors.companyPhone && (
                          <p className="text-sm text-red-500">{errors.companyPhone}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  // Password step (step 3)
                  <div className="space-y-6">
                    {/* Password */}
                    <div className="space-y-2">
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          value={formData.password}
                          onChange={(e) => handleInputChange("password", e.target.value)}
                          className="w-full h-12 px-4 pr-12 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-red-500">{errors.password}</p>
                      )}
                    </div>
                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm Password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                          className="w-full h-12 px-4 pr-12 bg-white border-0 border-b-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-0 focus:outline-none shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-4 mt-8">
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      onClick={() => setCurrentStep(currentStep - 1)}
                      className="flex-1 h-12 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg"
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={
                      isRegisterPending ||
                      isGeneratingOTP ||
                      (currentStep === 1 && !isStep1Valid) ||
                      (currentStep === 2 && !isStep2Valid) ||
                      (currentStep === 3 && !isStep3Valid)
                    }
                    className={`${currentStep > 1 ? 'flex-1' : 'w-full'} h-12 btn-emerald-gradient disabled:opacity-50 text-white font-semibold rounded-lg`}
                  >
                    {isRegisterPending
                      ? "Creating account..."
                      : isGeneratingOTP
                        ? "Sending OTP..."
                      : currentStep === 3
                        ? "Confirm"
                        : "Next"}
                  </Button>
                </div>

                {/* Only show login link if config is loaded and login is enabled */}
                {configLoaded && isLoginEnabled && (
                <div className="text-center text-sm text-gray-600 mt-6">
                  <span>Already Have an Account? </span>
                  <Link to="/login">
                    <span className="text-emerald-600 hover:underline cursor-pointer font-medium">
                      Login
                    </span>
                  </Link>
                </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;